import {
  Container,
  Box,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Tooltip,
} from "@material-ui/core";
// Remove the duplicate import
import React, { FC, useContext, useEffect, useState, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IdentityApplicationViewState } from "libs/resources/identity/applications/application-views/IdentityApplicationInfo";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import { EnvironmentType } from "header/environment-selector/ENV_CONSTANTS";
import { useSelector } from "react-redux";
import Alert from "@material-ui/lab/Alert";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from "@material-ui/icons/Close";
import CallbackLogoutField from "../../../applications/components/CallbackLogoutField";
import ApplicationService from "../../../applications/services/ApplicationService";
import Submit from "modus/components/form/submit/Submit";
import { SnackNotificationContext } from "legacy-components/notifications/GenericSnackNotification";
import { mapToArray } from "libs/utils/shared/common";
import _ from "lodash";
import { resolveAPIErrorMessage } from "modus/components/error-message/ErrorMessage";
import { ResourceAuthorizer } from "pages/home/authorizer/components/Authorizer";
import { useParams } from "react-router";
import { useAuthorization } from "pages/home/authorizer/components/AuthorizationProvider";
import { validateCallbackLogoutUrls } from "libs/utils/urlValidation";

interface GrantTypeProps {
  value: string;
  title: string;
  helperText: string;
}

interface ValidationResult {
    isValid: boolean;
    errors: string[];
  }

  interface FieldError {
    value: boolean;
    errors: string[];
  }

export const isGrantTypeEnabled = (
  grantType: any,
  selectedGrantTypes: string[],
  authenticationMethods: any[],
  canUpdateApplication: boolean
) => {
  if(!canUpdateApplication) {
    return false;
  }
  const primaryTypes = authenticationMethods
    .filter((method) => method.type === "primary")
    .map((method) => method.value);
  if (grantType.type === "primary") {
    return true;
  } else if (grantType.type === "secondary") {
    // If any primary grant type selected, then secondary grant types are enabled
    if (selectedGrantTypes.some((method) => primaryTypes.includes(method))) {
      return true;
    }
  }

  return false;
};
const FrontEndSupportedGrantType = "authorization_code";
export const useGrantTypeDetails = () => {
  const intl = useIntl();
  return [
    {
      value: "authorization_code",
      title: intl.formatMessage({
        defaultMessage: "Authorization Code Grant",
      }),
      helperText: intl.formatMessage({
        defaultMessage:
          "The Authorization Code Grant allows confidential and public clients to exchange an authorization code for an access token via a trusted back channel.",
      }),
      type: "primary",
    },
    {
      value: "client_credentials",
      title: intl.formatMessage({
        defaultMessage: "Client Credentials Grant",
      }),
      helperText: intl.formatMessage({
        defaultMessage:
          "The Client Credentials Grant allows clients to obtain an access token outside of the context of a user with a secured environments.",
      }),
      type: "primary",
    },
    {
      value: "urn:ietf:params:oauth:grant-type:device_code",
      title: intl.formatMessage({
        defaultMessage: "Device Code",
      }),
      helperText: intl.formatMessage({
        defaultMessage:
          "The Device Code grant type allows input-constrained or no-browser devices to exchange a previously obtained code for an access token.",
      }),
      type: "primary",
    },
    // TODO: This Grant type is needed for legacy applications if want to enable..
    /*{
            value: "implicit",
            title: intl.formatMessage({
                defaultMessage: "Implicit Grant"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "The Implicit Grant allows clients to obtain an access token without an intermediate code exchange step, rather than being returned via a trusted back channel like in the Authorization Code Grant."
            })
        },*/
    {
      value: "refresh_token",
      title: intl.formatMessage({
        defaultMessage: "Use Refresh Tokens",
      }),
      helperText: intl.formatMessage({
        defaultMessage:
          "A refresh token allows an application to obtain a new access token without prompting the user for specified amount of time.",
      }),
      type: "secondary",
    },
    {
      value: "urn:ietf:params:oauth:grant-type:token-exchange",
      title: intl.formatMessage({
        defaultMessage: "Token Exchange",
      }),
      helperText: intl.formatMessage({
        defaultMessage:
          "Sharing of identity and security information in heterogeneous environments or across security domains.",
      }),
      type: "secondary",
    },
  ];
};

interface GrantTypesFormProps {
  existingGrantTypes?: string[];
  state?: IdentityApplicationViewState;
  reloadApplication: () => void;
}
export const GrantTypesForm: FC<GrantTypesFormProps> = ({
  existingGrantTypes,
  state,
  reloadApplication,
}) => {
  const authenticationMethods = useGrantTypeDetails();
  const [updatedGrantTypes, setUpdatedGrantTypes] = useState<string[]>(
    existingGrantTypes ?? []
  );
  const [showFrontEndSupport, toggleFrontEndSupport] = useState<boolean>(false);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const { isActionAllowed } = useAuthorization("APPLICATION", state?.application?.applicationId as string);
  const canUpdateApplication = isActionAllowed("update");
  useEffect(() => {
    if (!existingGrantTypes || !setUpdatedGrantTypes) {
      return;
    }
    setUpdatedGrantTypes(existingGrantTypes);
  }, [setUpdatedGrantTypes, existingGrantTypes]);

  useEffect(() => {
    // Always check if Authorization Code Grant is included, regardless of array length
    if (updatedGrantTypes?.includes(FrontEndSupportedGrantType)) {
      toggleFrontEndSupport(true);
    } else {
      toggleFrontEndSupport(false);
      // Clear URL values when hiding the fields
      setFormValues((prev) => ({
        ...prev,
        callback: "",
        logout: "",
      }));
    }
  }, [updatedGrantTypes]);

  const onChange = (method: any) => {
    /* if (updatedGrantTypes.includes(method.value)) {
            setUpdatedGrantTypes(updatedGrantTypes.filter((value) => value !== method.value));
        } else {
            setUpdatedGrantTypes([...updatedGrantTypes , method.value]);
        }*/

    let currentGrantTypes;
    if (updatedGrantTypes.includes(method.value)) {
      currentGrantTypes = updatedGrantTypes.filter(
        (value) => value !== method.value
      );
    } else {
      currentGrantTypes = [...updatedGrantTypes, method.value];
    }
    // if no primary types selcted, then remove all secondary types
    if (
      !currentGrantTypes.includes("authorization_code") &&
      !currentGrantTypes.includes("client_credentials") &&
      !currentGrantTypes.includes(
        "urn:ietf:params:oauth:grant-type:device_code"
      ) &&
      (!currentGrantTypes.includes("refresh_token") ||
        !currentGrantTypes.includes(
          "urn:ietf:params:oauth:grant-type:token-exchange"
        ))
    ) {
      currentGrantTypes = currentGrantTypes.filter(
        (value) =>
          value !== "refresh_token" &&
          value !== "urn:ietf:params:oauth:grant-type:token-exchange"
      );
    }

    setUpdatedGrantTypes(currentGrantTypes);
  };

    const identityEnvironment = useSelector(
        (state: any) => state?.common?.identityEnvironment
      );
      const notification = useContext(SnackNotificationContext);
    
      
      const [updateState, { subscribe }] = useAsyncAction(() =>
        ApplicationService.updateApplication(
          identityEnvironment as EnvironmentType,
          state?.application?.applicationName as string,
          {
            grantTypes: updatedGrantTypes,
            logoutUri: showFrontEndSupport ? makeUrls(formValues.logout): [],
            redirectUri: showFrontEndSupport ? makeUrls(formValues.callback): [],
          }
        )
      );
    
      const [formValues, setFormValues] = React.useState({
        callback:
          state?.publishStatus?.[identityEnvironment as EnvironmentType]
            ?.redirectUri === undefined
            ? ""
            : state?.publishStatus?.[identityEnvironment as EnvironmentType]?.redirectUri?.join(", "),
    
        logout:
          state?.publishStatus?.[identityEnvironment as EnvironmentType]
            ?.logoutUri === undefined
            ? ""
            : state.publishStatus?.[identityEnvironment as EnvironmentType]?.logoutUri?.join(", "),
      });
    
      const [AlertClose, setAlertClose] = React.useState({ info: false, error: true });
    
      const callback_length = formValues.callback
        ?.split(",")
        .map((str) => str.trim())
        .filter((str) => str.length > 0).length;
    
      const logout_length = formValues.logout
        ?.split(",")
        .map((str) => str.trim())
        .filter((str) => str.length > 0).length;
    
      const [counter, setCounter] = React.useState({
        callback: callback_length === undefined ? 0 : callback_length,
        logout: formValues.logout === undefined ? 0 : logout_length,
      });
    
      const [error, setError] = React.useState<{
        callback: FieldError;
        logout: FieldError;
      }>({
        callback: { value: false, errors: [] },
        logout: { value: false, errors: [] },
      });
    
      const handleChange = (e: any) => {
        const { name, value } = e.target;
    
        setFormValues((prevValues) => ({
          ...prevValues,
          [name]: value,
        }));
      };
    
      useEffect(()=>{
    
        const callback_length = formValues.callback
          ?.split(",")
          .map((str) => str.trim())
          .filter((str) => str.length > 0).length;
        const logout_length = formValues.logout
          ?.split(",")
          .map((str) => str.trim())
          .filter((str) => str.length > 0).length;
    
        setCounter({
          callback: callback_length === undefined ? 0 : callback_length,
          logout: formValues.logout === undefined ? 0 : logout_length,
        });
    
        const callbackValidationError = validateCallbackLogoutUrls(formValues.callback);
        const logoutValidationError = validateCallbackLogoutUrls(formValues.logout);
        
        setError({
          callback: { 
            value: callbackValidationError !== "", 
            errors: callbackValidationError !== "" ? [callbackValidationError] : [] 
          },
          logout: { 
            value: logoutValidationError !== "", 
            errors: logoutValidationError !== "" ? [logoutValidationError] : [] 
          },
        });
      }, [formValues]);
    
      const makeUrls = useCallback((urlString?: string) =>
          urlString?.split(",").map((value) => String(value).trim()).filter((value) => value.length > 0) ?? []
      ,[]);
    
      
    
      useEffect(() => {
        if (!updateState.error) {
          return;
        }
        setAlertClose({ info: AlertClose.info, error: false });
      }, [AlertClose.info, notification, updateState.error]);
    
      useEffect(() => {
        if (!updateState.loaded || !notification) {
          return;
        }
        notification.show({
          id: "updatestate-success",
          message: <FormattedMessage defaultMessage="Updated Successfully." />,
          timeout: 4000,
        });
        reloadApplication();
      }, [ notification, updateState.loaded]);
    
     

  useEffect(() => {
    const validate = () => {
      // Disable submit if there are URL validation errors
      if (
        error.callback.value ||
        error.logout.value ||
        updatedGrantTypes.length === 0
      ) {
        setSubmitDisabled(true);
        return;
      }

      // Disable submit if no grant types are selected
      if (updatedGrantTypes.length === 0) {
        setSubmitDisabled(true);
        return;
      }

      // If Authorization Code Grant is selected, both URLs are required
      if (updatedGrantTypes.includes(FrontEndSupportedGrantType)) {
        const callbackUrls = formValues.callback?.trim();
        const logoutUrls = formValues.logout?.trim();
        if (!callbackUrls || !logoutUrls) {
          setSubmitDisabled(true);
          return;
        }
      }

            // Enable submit only if there are changes in grant types or URLs
            (!_.isEqual(_.sortBy(updatedGrantTypes), _.sortBy(existingGrantTypes)) ||
             !_.isEqual(state?.publishStatus?.[identityEnvironment as EnvironmentType]?.logoutUri?.join(", "),formValues.logout) ||
             !_.isEqual(state?.publishStatus?.[identityEnvironment as EnvironmentType]?.redirectUri?.join(", "),formValues.callback)) 
              ? setSubmitDisabled(false) : setSubmitDisabled(true);
         }
        validate();
    },[updatedGrantTypes, formValues.logout, formValues.callback, state, existingGrantTypes, identityEnvironment, error]);

    return (
        <Paper>
            <Box padding={2}>
            <Typography variant='h6'>Grant Types</Typography>
            {!canUpdateApplication && (
              <Alert 
                severity="warning"
                variant="outlined"
                style={{ marginTop: "16px", marginBottom: "16px" }}
              >
                Only application owner and team administrator can update the grant types
              </Alert>
            )}
            <List >
                {authenticationMethods.map((method, index) => (
                    <ListItem key={index}>
                    <ListItemIcon>
                        <Checkbox
                          color='primary' 
                          checked={updatedGrantTypes.includes(method.value)}
                          onChange={() => onChange(method)}
                          disabled={isGrantTypeEnabled(method, updatedGrantTypes, authenticationMethods, canUpdateApplication)? false : true}
                        />
                    </ListItemIcon>
                    <ListItemText  
                        primary={<Box style={{display: "flex",flexDirection: "row", columnGap: "10px",alignItems: "self-end"}}>
                          {method.title}
                          {method.value === "authorization_code" && (<Tooltip
                            title={<span>Use Authorization Code Grant to set up a user sign in screen for your application.</span>}
                            placement="right"
                          >
                            <InfoOutlinedIcon
                              color={"primary"}
                            />
                          </Tooltip>)}
                        </Box>}
                      />
                    
                </ListItem>
              ))}
            </List>
            </Box>
            {showFrontEndSupport && state && (
                <Box style={{ backgroundColor: "white" }}>
                <Container maxWidth={false} style={{ padding: "20px 20px" }}>
                  <Typography variant="h6">
                    Callback and Logout URLs
                  </Typography>
                  <form noValidate autoComplete="off">
                    <Box style={{ paddingRight: "15px" }}>
                      {!AlertClose.info && (
                        <Alert variant="outlined" severity="info"
                        style={{ color: "#0063A3", borderColor: "#0063A3", borderLeft: "12px solid #0063A3", margin: "16px 0px" }}
                          action={
                            <CloseIcon
                              onClick={() => {
                                setAlertClose({ info: true, error: AlertClose.error });
                              }}
                              style={{ color: "#0063A3", width: "16px" }}
                            />
                          }
                          icon={<InfoOutlinedIcon style={{ color: "#0063A3" }} />}
                        >
                          <Typography style={{ fontWeight: 700 }}>
                            You are allowed to register a total of 250 callback URLs and
                            250 logout URLs. Both Callback URLs and Logout URLs are required when Authorization Code Grant is selected

                          </Typography>
                        </Alert>
                      )}
                      <Container maxWidth={false} style={{ padding: "0px" }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <CallbackLogoutField
                              counter={counter.callback}
                              label={"Allowed Callback URLs"}
                              placeholder={"Add comma separated URLs to be allowed"}
                              value={formValues.callback}
                              name={"callback"}
                              onChange={handleChange}
                              setError={() => {}}
                              required={updatedGrantTypes.includes(FrontEndSupportedGrantType)}
                              customError={
                                !formValues.callback?.trim() && updatedGrantTypes.includes(FrontEndSupportedGrantType)
                                  ? { valid: true, message: "Callback URLs are required when Authorization Code Grant is selected" }
                                  : validateCallbackLogoutUrls(formValues.callback) !== ""
                                    ? { valid: true, message: validateCallbackLogoutUrls(formValues.callback) }
                                    : { valid: false, message: "" }
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <CallbackLogoutField
                              counter={counter.logout}
                              label={"Allowed Logout URLs"}
                              placeholder={"Add comma separated URLs to be allowed"}
                              value={formValues.logout}
                              name={"logout"}
                              onChange={handleChange}
                              setError={() => {}}
                              required={updatedGrantTypes.includes(FrontEndSupportedGrantType)}
                              customError={
                                !formValues.logout?.trim() && updatedGrantTypes.includes(FrontEndSupportedGrantType)
                                  ? { valid: true, message: "Logout URLs are required when Authorization Code Grant is selected" }
                                  : validateCallbackLogoutUrls(formValues.logout) !== ""
                                    ? { valid: true, message: validateCallbackLogoutUrls(formValues.logout) }
                                    : { valid: false, message: "" }
                              }
                            />
                          </Grid>
                        </Grid>
                      </Container>
                    </Box>
                    
                  </form>
                </Container>
              </Box>
            )}
            <Box py={2} pr={2}>
            
                      {/* Show validation messages */}
                      <Box m={2}>
                      {updateState.error && (
                          <Alert severity="error" variant="outlined">
                            {resolveAPIErrorMessage(updateState.errorValue, 
                              <FormattedMessage defaultMessage="Failed to update grant types. Please try again." />
                            )}
                          </Alert>
                        )}
                        {updatedGrantTypes.length === 0 && (
                          <Alert severity="error" variant="outlined" className={"prevalidation-error"}>
                            Please select at least one grant type
                          </Alert>
                        )}
                        
                        {validateCallbackLogoutUrls(formValues.callback) !== "" && (
                          <Alert severity="error" variant="outlined" className={"prevalidation-error"}>
                            Invalid Callback URLs: {validateCallbackLogoutUrls(formValues.callback)}
                          </Alert>
                        )}
                        {validateCallbackLogoutUrls(formValues.logout) !== "" && (
                          <Alert severity="error" variant="outlined" className={"prevalidation-error"}>
                            Invalid Logout URLs: {validateCallbackLogoutUrls(formValues.logout)}
                          </Alert>
                        )}
                      </Box>
              {canUpdateApplication && <Box display="flex" justifyContent="flex-end">
                        <Submit
                          variant="outlined"
                          color="primary"
                          state={updateState as any}
                          onClick={() => subscribe()}
                          disabled={submitDisabled}
                          style={{ width: "127px", height: "32px" }}
                        >
                          Submit
                        </Submit>
                      </Box>
              }
                    </Box>
        </Paper>
    );
}



export default GrantTypesForm;
